// lib/menu/menu.tsx
import { logDebug } from "@lune/common/logger";
import {
  Alert,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import hotkeys from "hotkeys-js";
import React2, { Fragment, useEffect, useState as useState2 } from "react";

// lib/keyboard/keyboardShortcut.tsx
import { Chip } from "@mui/material";
import { jsx } from "react/jsx-runtime";
var KeyboardShortcut = ({
  label
}) => /* @__PURE__ */ jsx(Chip, { label });

// lib/menu/submenu.tsx
import { Container } from "@mui/material";
import { useLayoutEffect, useState } from "react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var Submenu = ({
  item,
  itemContext,
  onNavigate
}) => {
  const hotkeyPressed = useHotkeys();
  const [items, setItems] = useState();
  useLayoutEffect(() => {
    setItems(item.itemsBuilder());
  }, [item]);
  return /* @__PURE__ */ jsxs("div", { children: [
    renderMenuItem(item.root, itemContext),
    items !== void 0 ? /* @__PURE__ */ jsx2(Container, { children: items.map(
      (item2, index) => renderMenuItem(item2, { index, hotkeyPressed, onNavigate, items })
    ) }) : /* @__PURE__ */ jsx2("div", { children: "NOTHING" })
  ] });
};

// lib/menu/variant.ts
function isMenuItem(object) {
  return object.text !== void 0 && object.target !== void 0;
}
function isWidgetItem(object) {
  return object.widget !== void 0;
}
function isDivider(object) {
  return object.divider !== void 0;
}
function isSubmenu(object) {
  return object.root !== void 0;
}
function isTitleItem(object) {
  return object.title !== void 0;
}
function isSubtitleItem(object) {
  return object.subtitle !== void 0;
}

// lib/menu/menu.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var selectedTab = (items, location2) => {
  const tabList = items.filter(
    (item) => isMenuItem(item) || isSubmenu(item)
  ).map((item) => isSubmenu(item) ? item.root : item).reduce((result, item, index) => {
    result[item.target.toString()] = index;
    return result;
  }, {});
  const match = Object.keys(tabList).filter(
    (targetPath) => location2.pathname === targetPath || `${location2.pathname}${location2.hash}` === targetPath
  );
  if (match.length === 1) {
    return tabList[match[0]];
  }
  return -1;
};
var useHotkeys = () => {
  const [hotkeyPressed, setHotkeyPressed] = useState2(false);
  useEffect(() => {
    const handleKeyChange = () => {
      setHotkeyPressed(hotkeys.alt || hotkeys.shift);
    };
    const handleBlur = () => {
      setHotkeyPressed(false);
    };
    window.addEventListener("keydown", handleKeyChange);
    window.addEventListener("keyup", handleKeyChange);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyChange);
      window.removeEventListener("keyup", handleKeyChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);
  return hotkeyPressed;
};
var Menu = ({ items, onNavigate }) => {
  const hotkeyPressed = useHotkeys();
  useEffect(() => {
    const boundKeys = [];
    let index = 0;
    items.filter(isMenuItem).forEach(({ target, hotkey }) => {
      const boundKey = hotkey !== void 0 ? `shift+${hotkey}` : `shift+${++index}`;
      logDebug(`Capturing '${boundKey}' to ${target}`);
      boundKeys.push(boundKey);
      hotkeys(boundKey, () => {
        void onNavigate(target);
      });
    });
    return () => {
      boundKeys.forEach((boundKey) => {
        hotkeys.unbind(boundKey);
      });
    };
  }, [items, onNavigate]);
  return /* @__PURE__ */ jsx3(React2.Fragment, { children: items.map((item, index) => {
    return isMenuItem(item) ? /* @__PURE__ */ jsx3(Fragment, { children: renderMenuItem(item, { index, hotkeyPressed, onNavigate, items }) }, index) : isWidgetItem(item) ? /* @__PURE__ */ jsx3(Fragment, { children: item.widget }, index) : isDivider(item) ? /* @__PURE__ */ jsx3(Divider, {}, index) : isSubmenu(item) ? /* @__PURE__ */ jsx3(
      Submenu,
      {
        item,
        itemContext: { index, hotkeyPressed, onNavigate, items },
        onNavigate
      },
      index
    ) : isTitleItem(item) ? /* @__PURE__ */ jsx3(
      Typography,
      {
        variant: "overline",
        sx: {
          px: 2,
          pt: 2,
          pb: 0.5,
          display: "block",
          fontWeight: 700,
          fontSize: "0.7rem",
          letterSpacing: "0.1em"
        },
        children: item.title
      },
      index
    ) : isSubtitleItem(item) ? /* @__PURE__ */ jsx3(
      Typography,
      {
        variant: "overline",
        sx: {
          px: 2,
          pt: 1,
          display: "block",
          color: "text.secondary",
          fontSize: "0.65rem",
          letterSpacing: "0.08em"
        },
        children: item.subtitle
      },
      index
    ) : /* @__PURE__ */ jsx3(Alert, { severity: "error", children: "Unknown item type" });
  }) });
};
var isSelected = (tabId, items) => {
  return tabId === selectedTab(items, location);
};
var renderMenuItem = ({ id, target, icon, text, disabled, hotkey }, context) => /* @__PURE__ */ jsxs2(
  ListItemButton,
  {
    selected: isSelected(id, context.items),
    onClick: () => {
      void context.onNavigate(target);
    },
    disabled,
    children: [
      /* @__PURE__ */ jsx3(ListItemIcon, { children: context.hotkeyPressed ? /* @__PURE__ */ jsx3(
        KeyboardShortcut,
        {
          label: hotkey !== void 0 ? hotkey.toLocaleUpperCase() : (context.index + 1).toString()
        }
      ) : icon }),
      /* @__PURE__ */ jsx3(ListItemText, { primary: text })
    ]
  },
  id
);

export {
  KeyboardShortcut,
  Submenu,
  isMenuItem,
  selectedTab,
  Menu
};
